import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import { determineAggregation } from '../../helpers/chartUtils';

// ---------- Types ----------

export interface PerAdTypeRow {
  group: string;
  insertions: number;
  conversions: number;
  revenue: number;
  hit_rate: number;
  avg_revenue_per_insertion: number;
  avg_etkp_ft: number;
  avg_etko_ft: number;
  ft_rows: number;
}

export interface DecisionQualityRow {
  winning_tier: number;
  winning_tier_name: string;
  insertions: number;
  revenue: number;
  avg_revenue_per_insertion: number;
  hit_rate: number;
}

export interface TopBanner {
  banner: string;
  ad_source: string;
  insertions: number;
  revenue: number;
  conversions: number;
}

export interface TopArticle {
  article_id: string;
  article_title: string;
  insertions: number;
  revenue: number;
  conversions: number;
}

export interface TimeSeriesPoint {
  date: string;
  webinar: number;
  plx: number;
  vnr: number;
  total: number;
}

export interface PreviousPeriod {
  date_from: string;
  date_to: string;
  total_insertions: number;
  total_revenue: number;
  total_conversions: number;
  avg_revenue_per_insertion: number;
  hit_rate: number;
}

export interface OverviewSummary {
  total_insertions?: number;
  total_revenue?: number;
  total_conversions?: number;
  avg_revenue_per_insertion?: number;
  hit_rate?: number;
  avg_cpa_per_webinar_registration?: number;
  per_ad_type?: PerAdTypeRow[];
  decision_quality?: DecisionQualityRow[];
  top_banners?: TopBanner[];
  top_articles?: TopArticle[];
  time_series_daily?: TimeSeriesPoint[];
  previous_period?: PreviousPeriod | null;
}

interface Props {
  summary: OverviewSummary | null;
  startDate: Date | null;
  endDate: Date | null;
}

// ---------- Constants ----------

const GROUP_LABELS: Record<string, string> = {
  webinar: 'Webinar',
  plx: 'Direkt-Werbung (PLX)',
  vnr: 'VNR Landing Pages',
};

const GROUP_COLORS: Record<string, string> = {
  webinar: '#1976d2',
  plx: '#7b1fa2',
  vnr: '#ed6c02',
};

const GROUP_CONV_LABEL: Record<string, string> = {
  webinar: 'Registrierungen',
  plx: 'Bestellungen',
  vnr: 'DOIs',
};

// ---------- Helpers ----------

const fmtEuro = (v: number | undefined | null) => `${(v || 0).toFixed(2).replace('.', ',')} €`;

const fmtInt = (v: number | undefined | null) => (v || 0).toLocaleString('de-DE');

const fmtPct = (v: number | undefined | null) => `${(v || 0).toFixed(1).replace('.', ',')} %`;

const isoWeek = (date: Date): string => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

// ---------- Components ----------

const KpiCard: React.FC<{
  label: string;
  value: string;
  tooltip: string;
  delta?: { value: number; isPct?: boolean } | null;
  deltaLabel?: string;
  accent?: string;
}> = ({ label, value, tooltip, delta, deltaLabel, accent }) => {
  const hasDelta = delta && typeof delta.value === 'number' && !isNaN(delta.value);
  const deltaColor = !hasDelta
    ? '#999'
    : delta!.value > 0
      ? '#2e7d32'
      : delta!.value < 0
        ? '#c62828'
        : '#666';
  return (
    <Card
      sx={{
        backgroundColor: '#ffffff',
        borderLeft: accent ? `4px solid ${accent}` : undefined,
        height: '100%',
      }}
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Tooltip title={tooltip} placement="top">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ cursor: 'help', fontSize: 11, textTransform: 'uppercase' }}
          >
            {label} ⓘ
          </Typography>
        </Tooltip>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'black', mt: 0.5 }}>
          {value}
        </Typography>
        {hasDelta ? (
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5} sx={{ color: deltaColor }}>
            {delta!.value > 0 ? (
              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
            ) : delta!.value < 0 ? (
              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
            ) : (
              <RemoveIcon sx={{ fontSize: 14 }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {delta!.value > 0 ? '+' : ''}
              {delta!.isPct
                ? `${delta!.value.toFixed(1).replace('.', ',')} pp`
                : `${delta!.value.toFixed(1).replace('.', ',')} %`}
            </Typography>
            {deltaLabel ? (
              <Typography variant="caption" color="text.secondary">
                {deltaLabel}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

const pctDelta = (current: number, previous: number): number | null => {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const AdTypeCard: React.FC<{ row: PerAdTypeRow }> = ({ row }) => {
  const color = GROUP_COLORS[row.group] || '#757575';
  return (
    <Card sx={{ backgroundColor: '#ffffff', borderTop: `3px solid ${color}` }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color }}>
          {GROUP_LABELS[row.group] || row.group}
        </Typography>
        <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
          <MetricLine
            label="Insertions"
            value={fmtInt(row.insertions)}
            tooltip="Anzahl der AI-generierten Ad-Insertions in diesem Zeitraum."
          />
          <MetricLine
            label={GROUP_CONV_LABEL[row.group] || 'Conversions'}
            value={fmtInt(row.conversions)}
            tooltip={
              row.group === 'webinar'
                ? 'Summe der Webinar-Registrierungen über alle Insertions dieses Typs.'
                : row.group === 'plx'
                  ? 'Summe der PLX-Bestellungen über alle Insertions dieses Typs.'
                  : 'Summe der bestätigten DOI-Opt-Ins über alle Insertions dieses Typs.'
            }
          />
          <MetricLine
            label="Revenue"
            value={fmtEuro(row.revenue)}
            tooltip={
              row.group === 'webinar'
                ? 'Σ (Reg. × Ø CPA 90T). Der globale 90-Tage-ØCPA je Webinar-Registrierung aus nicht-evergreen Webinaren wird auf alle Registrierungen angewendet.'
                : row.group === 'plx'
                  ? 'Σ CPA-Wert der Bestellungen (ohne CPA-Wert: 0). Jede Anfo trägt ihren eigenen Wert: aus ihrer Zahlung, sonst ihrem Angebot, sonst ihrem Produkt.'
                  : 'Σ vld.costs — tatsächliche VNR-Auszahlung aus vnr_lead_data. SOIs generieren keinen Revenue, da kein SOI-Preis existiert.'
            }
          />
          <MetricLine
            label="Ø Revenue / Insertion"
            value={fmtEuro(row.avg_revenue_per_insertion)}
            tooltip="Gesamt-Revenue / Anzahl Insertions. Misst mittlere Produktivität pro Ad-Slot dieser Kategorie."
            bold
          />
          <MetricLine
            label="Hit-Rate"
            value={fmtPct(row.hit_rate)}
            tooltip="% der Insertions mit Bestellung oder Wert > 0. Webinare haben oft viele kleine Treffer (hohe Rate), PLX oft wenige große Treffer (niedrige Rate, hoher Ø)."
            bold
          />
          <MetricLine
            label="Ø ETKP (nur FT)"
            value={row.ft_rows > 0 ? fmtEuro(row.avg_etkp_ft) : '—'}
            tooltip={`Mittelwert der ETKP über ${row.ft_rows} finanztrends-Insertions in dieser Kategorie. ETKP = Revenue/Empfänger × 1000. Nur für Finanztrends verfügbar.`}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

const MetricLine: React.FC<{
  label: string;
  value: string;
  tooltip: string;
  bold?: boolean;
}> = ({ label, value, tooltip, bold }) => (
  <Box display="flex" justifyContent="space-between" alignItems="baseline">
    <Tooltip title={tooltip} placement="left">
      <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', fontSize: 12 }}>
        {label} ⓘ
      </Typography>
    </Tooltip>
    <Typography
      variant="body2"
      sx={{
        fontWeight: bold ? 'bold' : 'normal',
        color: 'black',
        fontSize: 13,
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ---------- Main component ----------

const NewsletterAdInsightsOverview: React.FC<Props> = ({ summary, startDate, endDate }) => {
  const timeSeriesChart = useMemo(() => {
    const daily = summary?.time_series_daily || [];
    if (daily.length === 0 || !startDate || !endDate) {
      return {
        data: [] as Array<{
          date: string;
          Webinar: number;
          PLX: number;
          VNR: number;
        }>,
        label: 'täglich',
      };
    }
    const aggregation = determineAggregation(startDate, endDate);
    const bucket: Record<string, { webinar: number; plx: number; vnr: number }> = {};
    for (const p of daily) {
      const d = new Date(p.date + 'T00:00:00Z');
      let key: string;
      if (aggregation === 'daily') {
        key = p.date.slice(5); // MM-DD
      } else if (aggregation === 'weekly') {
        key = isoWeek(d);
      } else {
        key = p.date.slice(0, 7); // YYYY-MM
      }
      if (!bucket[key]) bucket[key] = { webinar: 0, plx: 0, vnr: 0 };
      bucket[key].webinar += p.webinar;
      bucket[key].plx += p.plx;
      bucket[key].vnr += p.vnr;
    }
    const sortedKeys = Object.keys(bucket).sort();
    const data = sortedKeys.map((k) => ({
      date: k,
      Webinar: Math.round(bucket[k].webinar * 100) / 100,
      PLX: Math.round(bucket[k].plx * 100) / 100,
      VNR: Math.round(bucket[k].vnr * 100) / 100,
    }));
    const label =
      aggregation === 'daily' ? 'täglich' : aggregation === 'weekly' ? 'wöchentlich' : 'monatlich';
    return { data, label };
  }, [summary?.time_series_daily, startDate, endDate]);

  if (!summary) return null;

  const prev = summary.previous_period;
  const deltaRevenue = prev ? pctDelta(summary.total_revenue || 0, prev.total_revenue) : null;
  const deltaInsertions = prev
    ? pctDelta(summary.total_insertions || 0, prev.total_insertions)
    : null;
  const deltaAvgRev = prev
    ? pctDelta(summary.avg_revenue_per_insertion || 0, prev.avg_revenue_per_insertion)
    : null;
  const deltaHit = prev ? (summary.hit_rate || 0) - prev.hit_rate : null; // absolute pp delta, not %
  const deltaConv = prev ? pctDelta(summary.total_conversions || 0, prev.total_conversions) : null;

  const compareLabel = prev ? 'vs Vorperiode' : '';

  return (
    <Box mb={3}>
      {/* KPI Cards Row */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={2.4}>
          <KpiCard
            label="Gesamt Revenue"
            value={fmtEuro(summary.total_revenue)}
            tooltip="Summe des CPA-Revenue aller Ad-Insertions im gewählten Zeitraum. Webinar: Reg. × Ø CPA (90-Tage-Schnitt, aktuell unten angezeigt). PLX: Σ CPA-Wert je Bestellung. VNR: Σ vld.costs (tatsächliche VNR-Auszahlung)."
            delta={deltaRevenue !== null ? { value: deltaRevenue } : null}
            deltaLabel={compareLabel}
            accent="#2e7d32"
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <KpiCard
            label="Ø Revenue / Insertion"
            value={fmtEuro(summary.avg_revenue_per_insertion)}
            tooltip="Gesamt-Revenue / Anzahl Insertions. Zeigt mittlere Produktivität pro Ad-Slot. Vergleich pro Ad-Typ unten in der Breakdown-Sektion."
            delta={deltaAvgRev !== null ? { value: deltaAvgRev } : null}
            deltaLabel={compareLabel}
            accent="#1976d2"
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <KpiCard
            label="Hit-Rate"
            value={fmtPct(summary.hit_rate)}
            tooltip="% der Insertions mit Bestellung oder Wert > 0. Zeigt wie oft die AI-Entscheidung monetarisierbar ist. Niedrig = viele leere Slots (AI-Tuning prüfen). Hoch = zuverlässig aber vielleicht nicht maximale Revenue/Insertion."
            delta={deltaHit !== null ? { value: deltaHit, isPct: true } : null}
            deltaLabel={compareLabel}
            accent="#ed6c02"
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <KpiCard
            label="Gesamt Insertions"
            value={fmtInt(summary.total_insertions)}
            tooltip="Anzahl aller AI-Ad-Insertions im Zeitraum (über alle Newsletter-Typen und Ad-Quellen). Conversions: ≈ Σ Registrierungen + Bestellungen + DOIs."
            delta={deltaInsertions !== null ? { value: deltaInsertions } : null}
            deltaLabel={compareLabel}
            accent="#6a1b9a"
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <KpiCard
            label="Ø CPA/Reg"
            value={fmtEuro(summary.avg_cpa_per_webinar_registration)}
            tooltip="Globaler 90-Tage-Ø CPA je Webinar-Registrierung (nur nicht-evergreen Webinare). Nur Referenz: der Webinar-Umsatz je Banner ist seit 16.09.2026 der CPA-Wert der tatsächlich zugeordneten Bestellungen."
            delta={null}
            accent="#757575"
          />
        </Grid>
      </Grid>

      {/* Time Series Chart */}
      <Card sx={{ backgroundColor: '#ffffff', mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Tooltip
              title="Revenue-Entwicklung als Stapel-Area: gesamt = oberer Rand, Anteile je Ad-Typ nach Farbe. Aggregation richtet sich nach Zeitraum-Länge: bis 45 Tage täglich, bis 120 Tage wöchentlich, darüber monatlich."
              placement="top"
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black', cursor: 'help' }}>
                Revenue-Entwicklung ({timeSeriesChart.label}) ⓘ
              </Typography>
            </Tooltip>
            <Box display="flex" gap={1}>
              <Chip
                label="Webinar"
                size="small"
                sx={{ backgroundColor: GROUP_COLORS.webinar, color: 'white' }}
              />
              <Chip
                label="PLX"
                size="small"
                sx={{ backgroundColor: GROUP_COLORS.plx, color: 'white' }}
              />
              <Chip
                label="VNR"
                size="small"
                sx={{ backgroundColor: GROUP_COLORS.vnr, color: 'white' }}
              />
            </Box>
          </Box>
          <Box height={300}>
            {timeSeriesChart.data.length > 0 ? (
              <ResponsiveBar
                data={timeSeriesChart.data}
                keys={['Webinar', 'PLX', 'VNR']}
                indexBy="date"
                margin={{ top: 10, right: 20, bottom: 50, left: 60 }}
                padding={0.3}
                groupMode="stacked"
                colors={({ id }) => {
                  if (id === 'Webinar') return GROUP_COLORS.webinar;
                  if (id === 'PLX') return GROUP_COLORS.plx;
                  if (id === 'VNR') return GROUP_COLORS.vnr;
                  return '#999';
                }}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -30,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  format: (v) =>
                    `${Number(v).toLocaleString('de-DE', {
                      maximumFractionDigits: 0,
                    })} €`,
                }}
                label={(d) => (Number(d.value) >= 50 ? `${Math.round(Number(d.value))} €` : '')}
                labelTextColor="#fff"
                tooltip={({ id, value, indexValue }) => (
                  <Box
                    sx={{
                      background: 'white',
                      padding: 1,
                      border: '1px solid #ccc',
                      fontSize: 12,
                    }}
                  >
                    <strong>{indexValue}</strong>
                    <br />
                    {id}: {fmtEuro(Number(value))}
                  </Box>
                )}
              />
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Typography color="text.secondary">Keine Daten für Chart</Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Per-Ad-Type Breakdown */}
      <Box mb={3}>
        <Tooltip
          title="Aggregierte KPIs je Ad-Typ-Gruppe. Vergleich zeigt unterschiedlichen Charakter: Webinare meist hohe Hit-Rate mit kleineren Treffern, PLX niedrige Hit-Rate mit großen Einzel-Treffern, VNR meist zuverlässig mit kleinen bis mittleren Treffern."
          placement="top"
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: 'black',
              mb: 1.5,
              cursor: 'help',
            }}
          >
            Breakdown nach Ad-Typ ⓘ
          </Typography>
        </Tooltip>
        <Grid container spacing={2}>
          {(summary.per_ad_type || []).map((row) => (
            <Grid item xs={12} md={4} key={row.group}>
              <AdTypeCard row={row} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* AI Decision Quality + Top Lists */}
      <Grid container spacing={2} mb={3}>
        {/* AI Decision Quality */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: '#ffffff', height: '100%' }}>
            <CardContent>
              <Tooltip
                title="Welche AI-Entscheidungs-Ebene hat gewonnen? Jede Insertion durchläuft eine Tier-Kette: Tier 1 ist die Primär-Wahl, höhere Tiers sind Fallbacks. Wenn Tier 1 systematisch mehr Revenue/Insertion als Fallbacks erzeugt → AI-Logik ist gesund. Wenn nicht → Primär-Tier-Regeln überprüfen."
                placement="top"
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: 'black',
                    mb: 1.5,
                    cursor: 'help',
                  }}
                >
                  AI Decision Quality ⓘ
                </Typography>
              </Tooltip>
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: 12,
                        }}
                      >
                        Tier
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: 12,
                        }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: 12,
                        }}
                      >
                        Insertions
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: 12,
                        }}
                      >
                        Revenue
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: 12,
                        }}
                      >
                        <Tooltip title="Revenue / Insertions dieser Tier-Stufe" placement="top">
                          <span style={{ cursor: 'help' }}>Ø/Ins ⓘ</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: 12,
                        }}
                      >
                        <Tooltip title="% der Insertions mit Bestellung oder Wert > 0" placement="top">
                          <span style={{ cursor: 'help' }}>Hit-Rate ⓘ</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(summary.decision_quality || []).map((row) => (
                      <TableRow key={row.winning_tier}>
                        <TableCell sx={{ color: 'black' }}>
                          {row.winning_tier >= 0 ? row.winning_tier : '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'black', fontSize: 13 }}>
                          {row.winning_tier_name}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'black' }}>
                          {fmtInt(row.insertions)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'black' }}>
                          {fmtEuro(row.revenue)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'black' }}>
                          {fmtEuro(row.avg_revenue_per_insertion)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'black' }}>
                          {fmtPct(row.hit_rate)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(summary.decision_quality || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                          Keine Daten
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top 5 Banner */}
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#ffffff', height: '100%' }}>
            <CardContent>
              <Tooltip
                title="Die 5 Banner mit dem höchsten Gesamt-Revenue im Zeitraum. Gleiche Banner werden oft in mehreren Insertions verwendet (verschiedene Artikel/Tage) — Revenue ist die Summe über alle ihre Insertions."
                placement="top"
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: 'black',
                    mb: 1.5,
                    cursor: 'help',
                  }}
                >
                  Top 5 Banner ⓘ
                </Typography>
              </Tooltip>
              {(summary.top_banners || []).length === 0 ? (
                <Typography color="text.secondary">Keine Daten</Typography>
              ) : (
                (summary.top_banners || []).map((b, idx) => (
                  <Box
                    key={b.banner}
                    py={0.5}
                    sx={{
                      borderBottom:
                        idx < (summary.top_banners?.length || 0) - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'black',
                        fontFamily: 'monospace',
                        fontSize: 11,
                      }}
                    >
                      {b.banner}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {b.insertions} Ins. · {b.conversions} Conv.
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        {fmtEuro(b.revenue)}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top 5 Artikel */}
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#ffffff', height: '100%' }}>
            <CardContent>
              <Tooltip
                title="Die 5 Artikel mit dem höchsten Gesamt-Revenue aus AI-Ad-Insertions. Pro Artikel gibt es meist eine Insertion pro Newsletter-Send — einige werden mehrfach eingesetzt."
                placement="top"
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: 'black',
                    mb: 1.5,
                    cursor: 'help',
                  }}
                >
                  Top 5 Artikel ⓘ
                </Typography>
              </Tooltip>
              {(summary.top_articles || []).length === 0 ? (
                <Typography color="text.secondary">Keine Daten</Typography>
              ) : (
                (summary.top_articles || []).map((a, idx) => (
                  <Box
                    key={a.article_id}
                    py={0.5}
                    sx={{
                      borderBottom:
                        idx < (summary.top_articles?.length || 0) - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'black', fontSize: 12 }}>
                      {a.article_title.length > 50
                        ? a.article_title.substring(0, 50) + '…'
                        : a.article_title}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {a.insertions} Ins. · {a.conversions} Conv.
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        {fmtEuro(a.revenue)}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewsletterAdInsightsOverview;
